import React, { ReactElement } from 'react';
import { saveData } from "services/api/saveData";
import { ModalProps } from "../ModalContext";
import InvoiceModal from "../../../views/invoices/InvoiceModal";
import ProposalModal from '../../../views/proposals/ProposalModal';
import OrderModal from '../../../views/order/OrderModal';
import JobModal from '../../../views/jobs/JobModal';
import { convertLocalDateTimeToUTC } from 'internationalization/timezoneConversions';

export const handleDuplicateDocument = async ({
    itemId,
    objectType,
    initializeModal,
    setFloatingAlert,
    setButtonLoader,
    buttonAction,
    userLocale,
    userTimezone,
    customApiUrl,
}: {
    itemId: number;
    objectType?: string;
    initializeModal: (content: ReactElement<any>, props?: ModalProps, itemId?: number | null) => void; 
    setFloatingAlert: Function;
    setButtonLoader: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
    buttonAction: string;
    userLocale: string;
    userTimezone: string;
    customApiUrl?: string;
}) => {

    // Show error if duplication is not implemented for the given object type
    if (objectType === undefined || !['proposal', 'order', 'job', 'invoice'].includes(objectType)) {
        setFloatingAlert({
            type: 'danger',
            message: 'Duplication is not implemented for this model.',
        });

        return;
    };

    // Modal component to object type mapping
    const modalComponentMapping: Record<string, React.ComponentType<{ itemId?: number }>> = {
        proposal: ProposalModal,
        order: OrderModal,
        job: JobModal,
        invoice: InvoiceModal,
    };

    try {
        // Start the button loader
        setButtonLoader((prev) => ({ ...prev, [buttonAction]: true }));

        // Add the current date to the data to set the order date
        const currentDateTime = new Date();
        const utcDateTime = convertLocalDateTimeToUTC(currentDateTime, userLocale, userTimezone);

        // Configure the submit data
        const submitData: Record<string, any> = {
            original_id: itemId,
            current_date: utcDateTime
        }

        // Call the api function
        const response = await saveData({
            apiUrl: customApiUrl ?? `duplicate_${objectType}`,
            method: 'post',
            data: submitData
        });

        // Open the new document after succesfully creating
        if (response && response.status === 201) {
            const newObjectId = response.data.id;

            if (newObjectId) {
                // Get the right modal component
                const ModalComponent = modalComponentMapping[objectType.toLowerCase()];

                // Initialize the modal
                initializeModal(
                    <ModalComponent itemId={newObjectId} />,
                    { modalSize: 'large', itemId: newObjectId }
                )
            }

            // Show success alert
            setFloatingAlert({
                type: 'success',
                message: `validation.${objectType}.created_successfully`,
            });
        }
    } catch (error) {
        setFloatingAlert({
            type: 'danger',
            message: `validation.${objectType}.creation_failed`,
        });
    } finally {
        // Stop the button loader
        setButtonLoader((prev) => ({ ...prev, [buttonAction]: false }));
    }
};